import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  schema
}: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    if (ogType) updateMetaTag('og:type', ogType, true);
    if (ogUrl) updateMetaTag('og:url', ogUrl, true);

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Update schema
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, schema]);
};

export const generateMusicSchoolSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "MusicSchool",
    "name": "矢谷和子バイオリン教室",
    "description": "東京都国分寺市で65年の伝統を誇るバイオリン教室。初心者から音楽大学を目指す方まで丁寧に指導。",
    "url": siteUrl,
    "telephone": "042-502-0408",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "国分寺市",
      "addressRegion": "東京都",
      "addressCountry": "JP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "35.7098",
      "longitude": "139.4789"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "09:30",
        "closes": "20:30"
      }
    ],
    "priceRange": "¥10,000-¥15,000",
    "areaServed": {
      "@type": "City",
      "name": "国分寺市"
    }
  };
};

export const generateWebPageSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "国分寺市バイオリン教室 矢谷和子バイオリン教室 | 東京都",
    "description": "東京都国分寺市で65年の伝統を誇る矢谷和子バイオリン教室。アットホームで明るい雰囲気の中、初心者から音楽大学を目指す方まで丁寧に指導。",
    "url": siteUrl,
    "inLanguage": "ja-JP",
    "isPartOf": {
      "@type": "WebSite",
      "name": "矢谷和子バイオリン教室",
      "url": siteUrl
    }
  };
};

export const generateFAQSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "初心者でも始められますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "はい、初心者の方も大歓迎です。一人ひとりのペースに合わせて丁寧に指導いたします。"
        }
      },
      {
        "@type": "Question",
        "name": "体験レッスンはありますか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "はい、無料体験レッスンを実施しております。お気軽にお問い合わせください。"
        }
      },
      {
        "@type": "Question",
        "name": "レッスン料金はいくらですか？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "月3回コース（30分）は10,000円から、月3回コース（45分以上）は15,000円からとなっております。入会金は5,000円です。"
        }
      }
    ]
  };
};
